// useSavedViews — TanStack Query hook covering list / create / delete on the
// platform `/v1/screens` surface.
//
// The host is responsible for the QueryClientProvider (every consumer of
// `@eidollon/ui-filters` already mounts one — `apps/terminal` does this in
// `routes/__root.tsx`). The host also supplies a `fetcher` so this package
// stays auth-agnostic — it never reads tokens from storage.
//
// Cache key: `['saved-views', entity]`. Entity-scoped so the Intel page's
// list and a future Vendor map's list don't clobber each other.

import type { filters as f, savedScreens } from '@eidollon/contracts';
import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { SavedScreenListResponseSchema } from './types.js';
import {
  DEFAULT_SAVED_VIEW_ENDPOINTS,
  type SavedScreenSummary,
  type SavedViewEndpoints,
} from './types.js';

export type SavedViewFetcher = (input: {
  url: string;
  method: 'GET' | 'POST' | 'DELETE';
  body?: unknown;
}) => Promise<unknown>;

export interface UseSavedViewsOptions {
  /** Filter-schema entity tag (e.g. "INTEL_SIGNAL"). Scopes the cache key. */
  entity: string;
  fetcher: SavedViewFetcher;
  endpoints?: SavedViewEndpoints;
  /** Disable the list query (e.g. while the host is gating render). */
  enabled?: boolean;
}

export interface UseSavedViewsResult {
  list: UseQueryResult<SavedScreenSummary[], Error>;
  create: UseMutationResult<{ id: string }, Error, CreateInput>;
  remove: UseMutationResult<void, Error, string>;
  /** TanStack queryKey for the list — exposed so callers can invalidate
   *  manually after an unrelated mutation (e.g. the AOI-alert toggle). */
  listKey: readonly unknown[];
}

export interface CreateInput {
  name: string;
  description?: string;
  filters: f.FilterState;
  is_shared?: boolean;
}

export function useSavedViews({
  entity,
  fetcher,
  endpoints,
  enabled = true,
}: UseSavedViewsOptions): UseSavedViewsResult {
  const ep = { ...DEFAULT_SAVED_VIEW_ENDPOINTS, ...endpoints };
  const qc = useQueryClient();
  const listKey = ['saved-views', entity] as const;

  const list = useQuery<SavedScreenSummary[], Error>({
    queryKey: listKey,
    enabled,
    queryFn: async () => {
      const raw = await fetcher({ url: ep.listUrl, method: 'GET' });
      const parsed = SavedScreenListResponseSchema.safeParse(raw);
      if (!parsed.success) {
        // Drop on parse fail — never feed unvalidated wire data into the UI.
        throw new Error(`saved-views list parse: ${parsed.error.message}`);
      }
      return parsed.data.items;
    },
  });

  const create = useMutation<{ id: string }, Error, CreateInput>({
    mutationFn: async (input) => {
      const body: savedScreens.SavedScreenCreateBody = {
        name: input.name,
        filters: input.filters,
        is_shared: input.is_shared ?? false,
        ...(input.description ? { description: input.description } : {}),
      };
      const raw = await fetcher({ url: ep.createUrl, method: 'POST', body });
      const r = raw as { id?: string } | null;
      if (!r || typeof r.id !== 'string') throw new Error('create: malformed response');
      return { id: r.id };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: listKey });
    },
  });

  const remove = useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await fetcher({
        url: ep.deleteUrl.replace(':id', encodeURIComponent(id)),
        method: 'DELETE',
      });
    },
    // Optimistic remove: drop the row immediately; rollback on error.
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: listKey });
      const prev = qc.getQueryData<SavedScreenSummary[]>(listKey);
      qc.setQueryData<SavedScreenSummary[]>(listKey, (old) =>
        (old ?? []).filter((s) => s.id !== id),
      );
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      const prev = (ctx as { prev?: SavedScreenSummary[] } | undefined)?.prev;
      if (prev) qc.setQueryData(listKey, prev);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: listKey });
    },
  });

  // Stable handle for callers that just want to refetch the list.
  // Returned for completeness; the list field already exposes refetch.
  useCallback(() => qc.invalidateQueries({ queryKey: listKey }), [qc, listKey]);

  return { list, create, remove, listKey };
}
